import { Reveal, RevealLine } from "@/components/Reveal";
import VitrineCarousel from "@/components/VitrineCarousel";
import { IMAGES } from "@/lib/sections";

// Three sub-collections stacked vertically. Each has its own numeral,
// title (italic emphasis word in gold), subtitle, top-right badge, and a
// caption-driven carousel identical to the "World" section carousels.
const SUBS = [
  {
    n: "01",
    title: "Icons of",
    accent: "Faith",
    body:
      "Sacred art in 24KT gold — Mecca & Medina, Bismillah, Mashallah and more.",
    badgeLabel: "Why Aarya24KT",
    badgeTitle: "Lifetime lustre.",
    badgeSub: "Never tarnishes, never needs polishing.",
    images: IMAGES.iconsOfFaith,
  },
  {
    n: "02",
    title: "Bespoke",
    accent: "Gifts",
    body:
      "Logo-personalised commissions, certified and presented in premium packaging.",
    badgeLabel: "Fully Customisable",
    badgeTitle: "Your logo, your language, your scale.",
    badgeSub: "24KT gold, delivered with certification.",
    images: IMAGES.bespokeGifts,
  },
  {
    n: "03",
    title: "The Dubai",
    accent: "Series",
    body: "Icons of the Emirates, rendered in pure 24KT gold foil.",
    badgeLabel: "Certified Purity",
    badgeTitle: "Certified pure 24KT gold foil.",
    badgeSub: "Authenticated craft, presentation-ready.",
    images: IMAGES.dubaiSeries,
  },
];

const Badge = ({ label, title, sub }) => (
  <div className="border border-gold/25 bg-ink/40 backdrop-blur-sm px-5 py-4 rounded-sm max-w-[260px]">
    <div className="num-overline text-gold/80 text-[9.5px]">{label}</div>
    <div className="mt-1.5 font-heading italic text-ivory text-[15px] leading-snug">
      {title}
    </div>
    <div className="mt-1.5 font-body text-beige/60 text-[11.5px] leading-snug">
      {sub}
    </div>
  </div>
);

export default function ExtendedCollections() {
  return (
    <section
      id="extended"
      data-testid="section-extended"
      className="deck-section relative bg-ink"
    >
      <div className="mx-auto w-full max-w-[1600px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
        {/* Parent section header — mirrors the World section headline pattern */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-7">
            <div className="num-overline">05 · Extended Collections</div>
            <h2 className="mt-8 font-heading text-ivory text-4xl md:text-5xl lg:text-[64px] leading-[1.05] tracking-tightest">
              Beyond the <span className="italic text-gold">world.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.15} className="lg:col-span-5">
            <p className="font-body text-beige/75 text-[15px] leading-[1.85] max-w-md">
              Three further collections — designed for markets where devotion,
              personalisation, and place matter most.
            </p>
          </Reveal>
        </div>

        {/* Three sub-collections, stacked vertically */}
        <div className="mt-16 md:mt-24 space-y-24 md:space-y-32">
          {SUBS.map((s, i) => (
            <div
              key={s.n}
              data-testid={`extended-sub-${s.n}`}
              className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start"
            >
              {/* Left copy column — mirrors World/BrandStory rhythm */}
              <div className="lg:col-span-5 order-2 lg:order-1">
                <Reveal delay={0.05}>
                  <div className="flex items-baseline gap-4">
                    <span className="font-heading italic text-gold text-lg">
                      {s.n}
                    </span>
                    <div className="h-px flex-1 bg-gold/20" />
                  </div>
                  <h3 className="mt-6 font-heading text-ivory text-3xl md:text-4xl lg:text-[44px] leading-[1.08] tracking-tightest">
                    {s.title}{" "}
                    <span className="italic text-gold">{s.accent}.</span>
                  </h3>
                  <p className="mt-5 font-body text-beige/70 text-[15px] leading-[1.85] max-w-md">
                    {s.body}
                  </p>
                </Reveal>
                <Reveal delay={0.15} className="mt-8">
                  <Badge
                    label={s.badgeLabel}
                    title={s.badgeTitle}
                    sub={s.badgeSub}
                  />
                </Reveal>
              </div>

              {/* Right carousel column */}
              <div className="lg:col-span-7 order-1 lg:order-2">
                <VitrineCarousel
                  images={s.images}
                  alt={`${s.title} ${s.accent}`}
                  mode="bleed"
                  aspect="aspect-[4/3] md:aspect-[16/11]"
                  delay={0.15 + i * 0.05}
                  intervalMs={3000}
                />
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24">
          <RevealLine />
        </div>
      </div>
    </section>
  );
}
